import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { IMPORT_EXIT_LEVELS_CSV } from '../graphql/mutations/exitLevels';
import { toast } from 'react-hot-toast';

const ImportExitLevelsModal = ({ isOpen, onClose, portfolioId, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('file');
  const [selectedFile, setSelectedFile] = useState(null);
  const [csvContent, setCsvContent] = useState('');
  const [isWorking, setIsWorking] = useState(false);
  const [result, setResult] = useState(null);

  const [importExitLevels] = useMutation(IMPORT_EXIT_LEVELS_CSV);
  // ESC handler and body scroll block like ImportTransactionsModal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setActiveTab('file');
      setSelectedFile(null);
      setCsvContent('');
      setIsWorking(false);
      setResult(null);
    }
  }, [isOpen]);

  const readFileText = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });

  const handleImport = async () => {
    if (!portfolioId) { toast.error('No portfolio selected'); return; }
    try {
      setIsWorking(true);
      let data = csvContent;
      if (activeTab === 'file') {
        if (!selectedFile) { toast.error('Select a CSV file'); setIsWorking(false); return; }
        data = await readFileText(selectedFile);
      }
      const { data: resp } = await importExitLevels({ variables: { portfolioId, csvData: data } });
      const res = resp?.importExitLevelsCSV;
      if (!res?.success) {
        toast.error(res?.error || 'Import failed');
      } else {
        toast.success(`Imported ${res.importedCount}, skipped ${res.skippedCount}`);
        if (onSuccess) onSuccess();
      }
      setResult(res);
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Import failed');
    } finally {
      setIsWorking(false);
    }
  };

  const handleReset = () => {
    setActiveTab('file');
    setSelectedFile(null);
    setCsvContent('');
    setIsWorking(false);
    setResult(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-md shadow-lg w-11/12 md:w-3/4 lg:w-1/2 mt-16 p-4">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Exit Levels - Import / Export</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">✕</button>
        </div>

        {/* No export button here; export handled via page header */}

        <div className="border-b border-gray-200 dark:border-gray-700 mb-3">
          <nav className="-mb-px flex space-x-6">
            <button onClick={() => setActiveTab('file')} disabled={!!result} className={`py-2 px-1 border-b-2 text-sm ${activeTab==='file'?'border-blue-500 text-blue-600 dark:text-blue-400':'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'} ${result ? 'opacity-50 cursor-not-allowed' : ''}`}>Upload File</button>
            <button onClick={() => setActiveTab('paste')} disabled={!!result} className={`py-2 px-1 border-b-2 text-sm ${activeTab==='paste'?'border-blue-500 text-blue-600 dark:text-blue-400':'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'} ${result ? 'opacity-50 cursor-not-allowed' : ''}`}>Paste Content</button>
          </nav>
        </div>

        {activeTab === 'file' ? (
          <div className="mb-4">
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              disabled={isWorking || !!result}
              className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 dark:file:bg-blue-900/20 dark:file:text-blue-300 hover:file:bg-blue-100 dark:hover:file:bg-blue-900/30 file:cursor-pointer cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        ) : (
          <div className="mb-4">
            <textarea rows={10} value={csvContent} onChange={(e)=>setCsvContent(e.target.value)} disabled={isWorking || !!result} placeholder="CoingeckoApiId,Price,Amount,Status\nbitcoin,1000,0.5,PLANNED\nethereum,2500,0.2,FILLED" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100" />
          </div>
        )}

        {result && (
          <div className="mb-3 p-3 rounded-md bg-gray-50 dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-200">
            <div className="mb-1">Imported: {result.importedCount} • Skipped: {result.skippedCount}</div>
            {Array.isArray(result.warnings) && result.warnings.length > 0 && (
              <div className="max-h-40 overflow-y-auto mt-2">
                {result.warnings.map((w, i) => (
                  <div key={i} className="text-xs text-yellow-800 dark:text-yellow-200">Row {w.rowNumber || '-'}: {w.message}</div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* CSV Format info block like transactions modal */}
        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
          <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
            <strong>CSV Format:</strong> CoingeckoApiId, Price, Amount, Status
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Status is optional and can be PLANNED, ORDER_SET, or FILLED. Defaults to PLANNED. Each row appends a new exit level for the token.
          </p>
        </div>

        {result?.success ? (
          <div className="flex justify-end gap-2">
            <button onClick={onClose} disabled={isWorking} className="px-3 py-1.5 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200">Close & Finish</button>
            <button onClick={handleReset} disabled={isWorking} className="px-3 py-1.5 rounded-md bg-blue-600 text-white">New Import</button>
          </div>
        ) : (
          <div className="flex justify-end gap-2">
            <button onClick={onClose} disabled={isWorking} className="px-3 py-1.5 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200">Close</button>
            <button onClick={handleImport} disabled={isWorking} className="px-3 py-1.5 rounded-md bg-blue-600 text-white">{isWorking? 'Working...' : 'Import CSV'}</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportExitLevelsModal;


